import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const categories = ['すべて', '制度改正', '申請資料', '試験', '統計', '分野別', 'イベント', '報道'];

const importanceStyle = {
    high: 'bg-red-50 text-red-700 border-red-200',
    normal: 'bg-blue-50 text-blue-700 border-blue-200',
    low: 'bg-slate-50 text-slate-600 border-slate-200',
};

const formatDate = (value) => {
    if (!value) return '日付未設定';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '日付未設定';
    return date.toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
};

const resolveSummary = (article) => {
    return article.summaryZh || article.summaryJa || article.relevanceReason || '概要はまだありません。';
};

export function NewsActionCard({ onClick }) {
    return (
        <button onClick={onClick} className="action-card action-card--teal">
            <div className="action-card-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h9l5 5v9a2 2 0 01-2 2z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 4v5h5M8 13h8M8 17h6" />
                </svg>
            </div>
            <span className="action-card-title">特定技能ニュース</span>
            <span className="action-card-subtitle">制度・試験・受入れ情報</span>
        </button>
    );
}

export default function SpecifiedSkillNewsPanel() {
    const { user } = useAuth();
    const [articles, setArticles] = useState([]);
    const [category, setCategory] = useState('すべて');
    const [statusFilter, setStatusFilter] = useState('Published');
    const [isLoading, setIsLoading] = useState(false);
    const [isMarkingAll, setIsMarkingAll] = useState(false);
    const [workingArticleId, setWorkingArticleId] = useState('');
    const [error, setError] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);
    const isSuperAdmin = user?.role === 'SuperAdmin';
    const isArchiveMode = isSuperAdmin && statusFilter === 'Archived';

    useEffect(() => {
        if (!isSuperAdmin && statusFilter !== 'Published') {
            setStatusFilter('Published');
        }
    }, [isSuperAdmin, statusFilter]);

    const loadNews = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await apiService.getSpecifiedSkillNews({
                page: 1,
                pageSize: 50,
                category: category === 'すべて' ? '' : category,
                status: isSuperAdmin ? statusFilter : 'Published',
            });
            setArticles(Array.isArray(response?.data) ? response.data : []);
            setUnreadCount(Number.isFinite(Number(response?.unreadCount)) ? Number(response.unreadCount) : 0);
        } catch (err) {
            console.error('Failed to load specified skill news', err);
            setError(err?.message || 'ニュースの取得に失敗しました');
        } finally {
            setIsLoading(false);
        }
    }, [category, isSuperAdmin, statusFilter]);

    useEffect(() => {
        loadNews();
    }, [loadNews]);

    const handleMarkAsRead = useCallback(async (articleId) => {
        try {
            const updated = await apiService.markSpecifiedSkillNewsRead(articleId);
            setArticles(prev => prev.map(article => (
                article.id === articleId ? { ...article, ...updated } : article
            )));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Failed to mark news as read', err);
            setError(err?.message || '既読にできませんでした');
        }
    }, []);

    const handleMarkAllAsRead = useCallback(async () => {
        setIsMarkingAll(true);
        setError('');
        try {
            await apiService.markAllSpecifiedSkillNewsRead(category === 'すべて' ? '' : category);
            setArticles(prev => prev.map(article => ({
                ...article,
                isRead: true,
                readAt: article.readAt || new Date().toISOString(),
            })));
            setUnreadCount(0);
        } catch (err) {
            console.error('Failed to mark all news as read', err);
            setError(err?.message || 'すべて既読にできませんでした');
        } finally {
            setIsMarkingAll(false);
        }
    }, [category]);

    const handleArchiveArticle = useCallback(async (article) => {
        setWorkingArticleId(article.id);
        setError('');
        try {
            await apiService.archiveSpecifiedSkillNews(article.id);
            setArticles(prev => prev.filter(item => item.id !== article.id));
            if (!article.isRead) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (err) {
            console.error('Failed to archive specified skill news', err);
            setError(err?.message || 'アーカイブできませんでした');
        } finally {
            setWorkingArticleId('');
        }
    }, []);

    const handleRestoreArticle = useCallback(async (article) => {
        setWorkingArticleId(article.id);
        setError('');
        try {
            await apiService.restoreSpecifiedSkillNews(article.id);
            setArticles(prev => prev.filter(item => item.id !== article.id));
        } catch (err) {
            console.error('Failed to restore specified skill news', err);
            setError(err?.message || '公開に戻せませんでした');
        } finally {
            setWorkingArticleId('');
        }
    }, []);

    const stats = useMemo(() => {
        const highCount = articles.filter(article => article.importance === 'high').length;
        const latest = articles[0]?.publishedAt || articles[0]?.publishedToDashboardAt || articles[0]?.createdAt;
        return {
            total: articles.length,
            highCount,
            unreadCount,
            latestLabel: latest ? formatDate(latest) : '未更新',
        };
    }, [articles, unreadCount]);

    const emptyMessage = isArchiveMode
        ? 'アーカイブ済みニュースはありません。'
        : 'まだ公開されたニュースはありません。';

    return (
        <div className="specified-news-shell">
            <div className="specified-news-header">
                <div>
                    <p className="specified-news-kicker">Specified Skilled Worker</p>
                    <h2 className="specified-news-title">特定技能ニュース</h2>
                </div>
                <button
                    type="button"
                    onClick={loadNews}
                    disabled={isLoading}
                    className="specified-news-refresh"
                    aria-label="ニュースを更新"
                    title="ニュースを更新"
                >
                    <svg className={isLoading ? 'animate-spin' : ''} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v6h6M20 20v-6h-6M5 19A9 9 0 0119 5M19 5h-5M5 19h5" />
                    </svg>
                </button>
            </div>

            {isSuperAdmin && (
                <div className="specified-news-mode-tabs" role="tablist" aria-label="ニュース状態">
                    <button
                        type="button"
                        onClick={() => setStatusFilter('Published')}
                        className={statusFilter === 'Published' ? 'is-active' : ''}
                    >
                        公開中
                    </button>
                    <button
                        type="button"
                        onClick={() => setStatusFilter('Archived')}
                        className={statusFilter === 'Archived' ? 'is-active' : ''}
                    >
                        アーカイブ
                    </button>
                </div>
            )}

            <div className="specified-news-stats">
                <div>
                    <span className="specified-news-stat-label">{isArchiveMode ? 'アーカイブ' : '公開中'}</span>
                    <strong>{stats.total}</strong>
                </div>
                <div>
                    <span className="specified-news-stat-label">{isArchiveMode ? '区分' : '未読'}</span>
                    <strong>{isArchiveMode ? '保管中' : stats.unreadCount}</strong>
                </div>
                <div>
                    <span className="specified-news-stat-label">重要</span>
                    <strong>{stats.highCount}</strong>
                </div>
                <div>
                    <span className="specified-news-stat-label">最新</span>
                    <strong>{stats.latestLabel}</strong>
                </div>
            </div>

            {!isArchiveMode && (
                <div className="specified-news-toolbar">
                    <span>{unreadCount > 0 ? `${unreadCount}件の未読があります` : 'すべて確認済みです'}</span>
                    <button
                        type="button"
                        onClick={handleMarkAllAsRead}
                        disabled={isMarkingAll || unreadCount === 0}
                    >
                        すべて既読
                    </button>
                </div>
            )}

            <div className="specified-news-tabs" role="tablist" aria-label="ニュースカテゴリ">
                {categories.map(item => (
                    <button
                        key={item}
                        type="button"
                        onClick={() => setCategory(item)}
                        className={item === category ? 'is-active' : ''}
                    >
                        {item}
                    </button>
                ))}
            </div>

            {error && (
                <div className="specified-news-error">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="specified-news-list">
                    {[...Array(4)].map((_, index) => (
                        <div key={index} className="specified-news-item specified-news-item--loading">
                            <div className="h-4 bg-slate-200 w-2/3 rounded mb-3"></div>
                            <div className="h-3 bg-slate-200 w-1/3 rounded mb-4"></div>
                            <div className="h-3 bg-slate-200 w-full rounded mb-2"></div>
                            <div className="h-3 bg-slate-200 w-5/6 rounded"></div>
                        </div>
                    ))}
                </div>
            ) : articles.length === 0 ? (
                <div className="specified-news-empty">
                    {emptyMessage}
                </div>
            ) : (
                <div className="specified-news-list">
                    {articles.map(article => {
                        const itemStateClass = isArchiveMode
                            ? 'is-archived'
                            : article.isRead ? 'is-read' : 'is-unread';

                        return (
                            <article key={article.id} className={`specified-news-item ${itemStateClass}`}>
                                <div className="specified-news-item-top">
                                    <div className="specified-news-meta">
                                        {!isArchiveMode && !article.isRead && <span className="specified-news-unread-dot">未読</span>}
                                        {isArchiveMode && <span className="specified-news-archive-dot">保管中</span>}
                                        <span>{formatDate(article.publishedAt || article.publishedToDashboardAt || article.createdAt)}</span>
                                        <span>{article.sourceName || '未設定'}</span>
                                    </div>
                                    <div className="specified-news-badges">
                                        <span className="specified-news-category">{article.category || 'その他'}</span>
                                        <span className={`specified-news-importance ${importanceStyle[article.importance] || importanceStyle.normal}`}>
                                            {article.importance === 'high' ? '重要' : article.importance === 'low' ? '低' : '通常'}
                                        </span>
                                    </div>
                                </div>

                                <h3>{article.title}</h3>
                                <p>{resolveSummary(article)}</p>

                                {Array.isArray(article.tags) && article.tags.length > 0 && (
                                    <div className="specified-news-tags">
                                        {article.tags.slice(0, 6).map(tag => (
                                            <span key={tag}>{tag}</span>
                                        ))}
                                    </div>
                                )}

                                <div className="specified-news-actions">
                                    <a
                                        href={article.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => {
                                            if (!isArchiveMode && !article.isRead) {
                                                handleMarkAsRead(article.id);
                                            }
                                        }}
                                        className="specified-news-link"
                                    >
                                        原文を開く
                                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17L17 7M9 7h8v8" />
                                        </svg>
                                    </a>
                                    {!isArchiveMode && !article.isRead && (
                                        <button
                                            type="button"
                                            onClick={() => handleMarkAsRead(article.id)}
                                            className="specified-news-read-button"
                                        >
                                            既読にする
                                        </button>
                                    )}
                                    {isSuperAdmin && !isArchiveMode && (
                                        <button
                                            type="button"
                                            onClick={() => handleArchiveArticle(article)}
                                            disabled={workingArticleId === article.id}
                                            className="specified-news-archive-button"
                                        >
                                            アーカイブ
                                        </button>
                                    )}
                                    {isSuperAdmin && isArchiveMode && (
                                        <button
                                            type="button"
                                            onClick={() => handleRestoreArticle(article)}
                                            disabled={workingArticleId === article.id}
                                            className="specified-news-restore-button"
                                        >
                                            公開に戻す
                                        </button>
                                    )}
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
